import type { BadgeCategory, DigitalPassport, Pathway, Badge } from '@/types/achievement';

// Helper function to create a Badge with required fields
const createBadge = (badge: {
  id: number;
  name: string;
  image: string;
  date: string;
  pathway: string;
  category: string;
  microskill: string;
}): Badge => ({
  id: badge.id,
  uuid: `badge-${badge.id}`,
  name: badge.name,
  image_url: badge.image,
  earned_at: badge.date,
  expires_at: null,
  verification_url: `https://skillsture.com/badge/${badge.id}`,
  date: badge.date,
  image: badge.image,
  category: badge.category,
  pathway: badge.pathway,
  microskill: badge.microskill,
});

export const badgeCategories: BadgeCategory[] = [
  {
    category: "Big Data",
    badges: [
      createBadge({ id:1, name: "Diagramming Language (Python)", image: "/badge/big-data/hs13-bd.png", date: "2024-01-12", pathway: "Junior Data Scientist", category: "Big Data", microskill: "Data Science Fundamentals"  }),
      createBadge({ id:12, name: "Data Structures & Algorithms", image: "/badge/big-data/hs9-bd.png", date: "2024-01-15", pathway: "Junior Data Scientist", category: "Big Data", microskill: "Data Science Fundamentals"  }),
      createBadge({ id:123, name: "Machine Learning with Python", image: "/badge/big-data/hs13-bd.png", date: "2024-01-18", pathway: "Junior Data Scientist", category: "Big Data", microskill: "Data Science Fundamentals"  }),
      createBadge({ id:1234, name: "Data Visualization with Power BI", image: "/badge/big-data/hs9-bd.png", date: "2024-01-20", pathway: "Junior Data Analyst & Scientist", category: "Big Data", microskill: "Data Optimization"  }),
      createBadge({ id:12345, name: "ETL or Extract Transform and Load", image: "/badge/big-data/hs13-bd.png", date: "2024-01-22", pathway: "Junior Data Analyst & Scientist", category: "Big Data", microskill: "Data Optimization"  })
    ]
  },
  {
    category: "Marketing",
    badges: [
      createBadge({ id:2, name: "Content Marketing", image: "/badge/product-marketing/hs58-dm.png", date: "2024-01-05", pathway: "Junior Data Analyst & Scientist", category: "Marketing", microskill: "Data Optimization"  }),
      createBadge({ id:22, name: "Digital Funnel", image: "/badge/product-marketing/hs58-dm.png", date: "2024-01-12", pathway: "Junior Data Analyst & Scientist", category: "Marketing", microskill: "Data Optimization" }),
      createBadge({ id:222, name: "Marketing Trend & Tools", image: "/badge/product-marketing/hs58-dm.png", date: "2024-01-15", pathway: "Junior Data Scientist", category: "Marketing", microskill: "Data Science Fundamentals"  }),
      createBadge({ id:2222, name: "Conversion Tracking", image: "/badge/product-marketing/hs58-dm.png", date: "2024-01-18", pathway: "Junior Data Analyst & Scientist", category: "Marketing", microskill: "Data Optimization"  }),
      createBadge({ id:22222, name: "Social Media Audience Profile", image: "/badge/product-marketing/hs58-dm.png", date: "2024-01-20", pathway: "Junior Data Analyst & Scientist", category: "Marketing", microskill: "Data Optimization"  }),
      createBadge({ id:222222, name: "Introduction to Digital Marketing", image: "/badge/product-marketing/hs58-dm.png", date: "2024-01-22", pathway: "Junior Data Analyst & Scientist", category: "Marketing", microskill: "Data Optimization"  })
    ]
  },
  {
    category: "Cybersecurity",
    badges: [
      createBadge({ id:3, name: "Network Security", image: "/badge/cybersecurity/hs22-cs.png", date: "2024-01-05", pathway: "Junior Data Analyst & Scientist", category: "Cybersecurity", microskill: "Data Optimization" }),
      createBadge({ id:33, name: "NIST Cybersecurity Framework", image: "/badge/cybersecurity/hs22-cs.png", date: "2024-01-22", pathway: "Junior Data Analyst & Scientist", category: "Cybersecurity", microskill: "Data Optimization" }),
      createBadge({ id:333, name: "Threat Analysis", image: "/badge/cybersecurity/hs22-cs.png", date: "2024-01-23", pathway: "Junior Data Analyst & Scientist", category: "Cybersecurity", microskill: "Data Optimization" })
    ]
  }
];

export const availablePathways: Pathway[] = [
  {
    image: "/badge/big-data/machine-learning-engineer.png",
    role: "Machine Learning Engineer",
    field: "Big Data",
    description: "Deploy and maintain ML models in production",
    badgesCount: "1",
    badgesTotal: "6",
    progress: "1/6 badges",
    completion: "80% remaining"
  }
];

export const earnedPassports: DigitalPassport[] = [
  { 
    image: "/badge/big-data/junior-data-analyst.png",
    role: "Junior Data Analyst & Scientist",
    field: "Big Data",
    passportId: "SKILLSTURE-DS-2024-001",
    verifiedBy: "PEOPLElogy Digital Sdn Bhd",
    verificationUrl: "https://skillsture.com/passport/SKILLSTURE-DS-2024-001",
    gallery: [
      "pages/achievements/digital-microlerning-role-passport.png",
      "pages/achievements/skilltrue-passport.png",
    ]
  }
];

export const recentBadges: Badge[] = [
  createBadge({ id:4, name: "Data Optimization Skills", date: "2024-01-12", image: "/badge/big-data/hs13-bd.png", pathway: "Junior Data Scientist", category: "Big Data", microskill: "Data Science Fundamentals" }),
  createBadge({ id:44, name: "Data Modelling", date: "2024-01-15", image: "/badge/big-data/hs9-bd.png", pathway: "Junior Data Scientist", category: "Big Data", microskill: "Data Science Fundamentals" }),
  createBadge({ id:444, name: "Cloud Security", date: "2024-01-18", image: "/badge/cloud-computing/hs31-cc.png", pathway: "Junior Data Analyst & Scientist", category: "Big Data", microskill: "Data Optimization" }),
  createBadge({ id:4444, name: "Adobe Indesign", date: "2024-01-20", image: "/badge/digital-product-design/hs39-pd.png", pathway: "Junior Data Analyst & Scientist", category: "Big Data", microskill: "Data Optimization" }),
  createBadge({ id:44444, name: "Conversation Tracking", date: "2024-01-22", image: "/badge/product-marketing/hs58-dm.png", pathway: "Junior Data Analyst & Scientist", category: "Big Data", microskill: "Data Optimization" }),
  createBadge({ id:444444, name: "Threat Analysis", date: "2024-01-05", image: "/badge/cybersecurity/hs22-cs.png", pathway: "Junior Data Analyst & Scientist", category: "Big Data", microskill: "Data Optimization" })
];

export const digitalPassports: DigitalPassport[] = [
  {
    image: "/badge/big-data/junior-data-analyst.png",
    role: "Junior Data Scientist",
    field: "Big Data",
    passportId: "SKILLSTURE-DS-2024-001",
    skillstree_name: "Data Science Fundamentals",
    verifiedBy: "PEOPLEbey Digital Sdn Bhd",
    badgesCount: "5",
    microskillsCount: "10",
    microskillsTotal: "11",
    completedDate: "2024-01-08",
    verificationUrl: "https://skillsture.com/passport/SKILLSTURE-DS-2024-001",
    gallery: [
      "pages/achievements/digital-microlerning-role-passport.png",
      "pages/achievements/skilltrue-passport.png",
    ],
    courses: [
      {
        name: "Skillstree: Data Science Fundamentals",
        verifiedBy: "PEOPLEbey Digital Sdn Bhd",
        badgesEarned: "5",
        totalBadges: "5",
        provider: "Microsoft®",
        completedDate: "2024-01-20"
      },
      {
        name: "Data Science Advanced",
        verifiedBy: "PEOPLEbey Digital Sdn Bhd",
        badgesEarned: "6",
        totalBadges: "6",
        provider: "Microsoft®",
        completedDate: "2024-01-08"
      }
    ]
  },
  {
    image: "/badge/product-marketing/digital-marketing-strategist.png",
    role: "Digital Marketing Specialist",
    field: "Digital Marketing",
    passportId: "SKILLSTURE-DM-2024-001",
    skillstree_name: "Digital Marketing Specialist",
    verifiedBy: "PEOPLEbey Digital Sdn Bhd",
    badgesCount: "5",
    microskillsCount: "10",
    microskillsTotal: "11",
    completedDate: "2024-01-28",
    verificationUrl: "https://skillsture.com/passport/SKILLSTURE-DS-2024-001",
    gallery: [
      "pages/achievements/digital-microlerning-role-passport.png",
      "pages/achievements/skilltrue-passport.png",
    ],
    courses: [
      {
        name: "Skillstree: Digital Marketing Specialist",
        verifiedBy: "PEOPLEbey Digital Sdn Bhd",
        badgesEarned: "6",
        totalBadges: "6",
        provider: "Microsoft®",
        completedDate: "2024-01-08"
      }
    ]
  },
  {
    image: "/badge/product-marketing/digital-marketing-strategist.png",
    role: "Digital Marketing Specialist",
    field: "Digital Marketing",
    passportId: "SKILLSTURE-DM-2024-001",
    skillstree_name: "Digital Marketing Specialist",
    verifiedBy: "PEOPLEbey Digital Sdn Bhd",
    badgesCount: "5",
    microskillsCount: "10",
    microskillsTotal: "11",
    completedDate: "2024-01-28",
    verificationUrl: "https://skillsture.com/passport/SKILLSTURE-DS-2024-001",
    gallery: [
      "pages/achievements/digital-microlerning-role-passport.png",
      "pages/achievements/skilltrue-passport.png",
    ],
    courses: [
      {
        name: "Skillstree: Digital Marketing Specialist",
        verifiedBy: "PEOPLEbey Digital Sdn Bhd",
        badgesEarned: "6",
        totalBadges: "6",
        provider: "Microsoft®",
        completedDate: "2024-01-08"
      }
    ]
  },
  {
    image: "/badge/product-marketing/digital-marketing-strategist.png",
    role: "Digital Marketing Specialist",
    field: "Digital Marketing",
    passportId: "SKILLSTURE-DM-2024-001",
    skillstree_name: "Digital Marketing Specialist",
    verifiedBy: "PEOPLEbey Digital Sdn Bhd",
    badgesCount: "5",
    microskillsCount: "10",
    microskillsTotal: "11",
    completedDate: "2024-01-28",
    verificationUrl: "https://skillsture.com/passport/SKILLSTURE-DS-2024-001",
    gallery: [
      "pages/achievements/digital-microlerning-role-passport.png",
      "pages/achievements/skilltrue-passport.png",
    ],
    courses: [
      {
        name: "Skillstree: Digital Marketing Specialist",
        verifiedBy: "PEOPLEbey Digital Sdn Bhd",
        badgesEarned: "6",
        totalBadges: "6",
        provider: "Microsoft®",
        completedDate: "2024-01-08"
      }
    ]
  },
  {
    image: "/badge/product-marketing/digital-marketing-strategist.png",
    role: "Digital Marketing Specialist",
    field: "Digital Marketing",
    passportId: "SKILLSTURE-DM-2024-001",
    skillstree_name: "Digital Marketing Specialist",
    verifiedBy: "PEOPLEbey Digital Sdn Bhd",
    badgesCount: "5",
    microskillsCount: "10",
    microskillsTotal: "11",
    completedDate: "2024-01-28",
    verificationUrl: "https://skillsture.com/passport/SKILLSTURE-DS-2024-001",
    gallery: [
      "pages/achievements/digital-microlerning-role-passport.png",
      "pages/achievements/skilltrue-passport.png",
    ],
    courses: [
      {
        name: "Skillstree: Digital Marketing Specialist",
        verifiedBy: "PEOPLEbey Digital Sdn Bhd",
        badgesEarned: "6",
        totalBadges: "6",
        provider: "Microsoft®",
        completedDate: "2024-01-08"
      }
    ]
  },
  {
    image: "/badge/product-marketing/digital-marketing-strategist.png",
    role: "Digital Marketing Specialist",
    field: "Digital Marketing",
    passportId: "SKILLSTURE-DM-2024-001",
    skillstree_name: "Digital Marketing Specialist",
    verifiedBy: "PEOPLEbey Digital Sdn Bhd",
    badgesCount: "5",
    microskillsCount: "10",
    microskillsTotal: "11",
    completedDate: "2024-01-28",
    verificationUrl: "https://skillsture.com/passport/SKILLSTURE-DS-2024-001",
    gallery: [
      "pages/achievements/digital-microlerning-role-passport.png",
      "pages/achievements/skilltrue-passport.png",
    ],
    courses: [
      {
        name: "Skillstree: Digital Marketing Specialist",
        verifiedBy: "PEOPLEbey Digital Sdn Bhd",
        badgesEarned: "6",
        totalBadges: "6",
        provider: "Microsoft®",
        completedDate: "2024-01-08"
      }
    ]
  },
  {
    image: "/badge/product-marketing/digital-marketing-strategist.png",
    role: "Digital Marketing Specialist",
    field: "Digital Marketing",
    passportId: "SKILLSTURE-DM-2024-001",
    skillstree_name: "Digital Marketing Specialist",
    verifiedBy: "PEOPLEbey Digital Sdn Bhd",
    badgesCount: "5",
    microskillsCount: "10",
    microskillsTotal: "11",
    completedDate: "2024-01-28",
    verificationUrl: "https://skillsture.com/passport/SKILLSTURE-DS-2024-001",
    gallery: [
      "pages/achievements/digital-microlerning-role-passport.png",
      "pages/achievements/skilltrue-passport.png",
    ],
    courses: [
      {
        name: "Skillstree: Digital Marketing Specialist",
        verifiedBy: "PEOPLEbey Digital Sdn Bhd",
        badgesEarned: "6",
        totalBadges: "6",
        provider: "Microsoft®",
        completedDate: "2024-01-08"
      }
    ]
  },
  {
    image: "/badge/product-marketing/digital-marketing-strategist.png",
    role: "Digital Marketing Specialist",
    field: "Digital Marketing",
    passportId: "SKILLSTURE-DM-2024-001",
    skillstree_name: "Digital Marketing Specialist",
    verifiedBy: "PEOPLEbey Digital Sdn Bhd",
    badgesCount: "5",
    microskillsCount: "10",
    microskillsTotal: "11",
    completedDate: "2024-01-28",
    verificationUrl: "https://skillsture.com/passport/SKILLSTURE-DS-2024-001",
    gallery: [
      "pages/achievements/digital-microlerning-role-passport.png",
      "pages/achievements/skilltrue-passport.png",
    ],
    courses: [
      {
        name: "Skillstree: Digital Marketing Specialist",
        verifiedBy: "PEOPLEbey Digital Sdn Bhd",
        badgesEarned: "6",
        totalBadges: "6",
        provider: "Microsoft®",
        completedDate: "2024-01-08"
      }
    ]
  }
];